import type { BankConnection, InvestmentConnection, Transaction } from '@fd/schemas';
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  CalendarClock,
  CornerDownLeft,
  Eye,
  EyeOff,
  Landmark,
  Languages,
  LayoutDashboard,
  Link2,
  Moon,
  PiggyBank,
  RefreshCw,
  Search,
  Settings,
  Sun,
  Target,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { bankingApi } from '../app/banking.ts';
import { useCategories, useEntities } from '../app/data.ts';
import { useI18n } from '../app/i18n.tsx';
import { investmentsApi } from '../app/investments.ts';
import { navigate, type Page, type Route } from '../app/router.tsx';
import { useSession } from '../app/session.tsx';
import { useTheme } from '../app/theme.tsx';
import type { QuickAddKind } from '../pages/QuickAdd.tsx';
import { MerchantIcon } from '../ui/BrandLogo.tsx';
import { Money, useScrollLock, useToast } from '../ui/primitives.tsx';

// Ctrl/Cmd + K: one place to go anywhere, do the common things and find any transaction — by
// name or by amount. Arrow keys move, Enter runs, Escape closes.

type Item = { id: string; label: string; icon: ReactNode; keywords?: string; hint?: ReactNode; run(): void | Promise<void> };
type Group = { key: 'actions' | 'pages' | 'transactions'; items: Item[] };

const fold = (text: string) =>
  text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/ł/g, 'l');

const PAGES: readonly { page: Page; icon: typeof LayoutDashboard }[] = [
  { page: 'dashboard', icon: LayoutDashboard },
  { page: 'transactions', icon: ArrowLeftRight },
  { page: 'accounts', icon: Wallet },
  { page: 'recurring', icon: CalendarClock },
  { page: 'budgets', icon: Target },
  { page: 'loans', icon: Landmark },
  { page: 'settlements', icon: Users },
  { page: 'people', icon: Users },
  { page: 'savings', icon: PiggyBank },
  { page: 'netWorth', icon: TrendingUp },
  { page: 'settings', icon: Settings },
];

export function CommandPalette({ open, onClose, onQuickAdd }: { open: boolean; onClose(): void; onQuickAdd(kind?: QuickAddKind): void }) {
  const { t, language, setLanguage } = useI18n();
  const { resolved, setTheme, privacy, setPrivacy } = useTheme();
  const { householdId, requestSync } = useSession();
  const toast = useToast();
  const transactions = useEntities<Transaction>('transaction');
  const categories = useCategories();
  const banks = useEntities<BankConnection>('bankConnection');
  const investments = useEntities<InvestmentConnection>('investmentConnection');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const list = useRef<HTMLDivElement | null>(null);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
  }, [open]);

  const groups = useMemo<Group[]>(() => {
    const needle = fold(query.trim());
    const matches = (item: Item) => !needle || fold(`${item.label} ${item.keywords ?? ''}`).includes(needle);
    const refreshAll = async (ids: string[], call: (householdId: string, id: string) => Promise<unknown>) => {
      if (!householdId) return;
      await Promise.allSettled(ids.map((id) => call(householdId, id)));
      requestSync();
      toast(t.t('palette.refreshed'), <RefreshCw aria-hidden="true" />);
    };
    const liveBanks = (banks.data ?? []).filter((c) => c.deletedAt === null && c.status !== 'DISCONNECTED').map((c) => c.id);
    const liveInvest = (investments.data ?? []).filter((c) => c.deletedAt === null && c.status !== 'DISCONNECTED').map((c) => c.id);

    const actions: Item[] = [
      { id: 'expense', label: t.t('palette.addExpense'), icon: <ArrowUpRight />, keywords: 'new add dodaj nowy', hint: <kbd>N</kbd>, run: () => onQuickAdd('EXPENSE') },
      { id: 'income', label: t.t('palette.addIncome'), icon: <ArrowDownLeft />, keywords: 'new add dodaj nowy', run: () => onQuickAdd('INCOME') },
      { id: 'transfer', label: t.t('palette.addTransfer'), icon: <ArrowLeftRight />, keywords: 'przelew transfer', run: () => onQuickAdd('TRANSFER') },
      { id: 'bank', label: t.t('palette.connectBank'), icon: <Link2 />, keywords: 'open banking bank lloyds', run: () => navigate({ page: 'accounts', bankConnect: '1' }) },
      ...(liveBanks.length > 0 ? [{ id: 'banks', label: t.t('palette.refreshBanks'), icon: <RefreshCw />, keywords: 'sync synchronizuj', run: () => refreshAll(liveBanks, bankingApi.refresh) }] : []),
      ...(liveInvest.length > 0
        ? [{ id: 't212', label: t.t('palette.refreshInvestments'), icon: <RefreshCw />, keywords: 'trading 212 isa sync', run: () => refreshAll(liveInvest, investmentsApi.refresh) }]
        : [{ id: 't212-connect', label: t.t('palette.connectInvestments'), icon: <Link2 />, keywords: 'trading 212 isa invest api', run: () => navigate({ page: 'savings' }) }]),
      {
        id: 'theme',
        label: t.t(resolved === 'dark' ? 'palette.themeLight' : 'palette.themeDark'),
        icon: resolved === 'dark' ? <Sun /> : <Moon />,
        keywords: 'theme motyw dark light ciemny jasny',
        run: () => setTheme(resolved === 'dark' ? 'light' : 'dark'),
      },
      { id: 'privacy', label: t.t(privacy ? 'palette.privacyOff' : 'palette.privacyOn'), icon: privacy ? <Eye /> : <EyeOff />, keywords: 'privacy prywatność ukryj', run: () => setPrivacy(!privacy) },
      { id: 'language', label: t.t('palette.languageSwitch'), icon: <Languages />, keywords: 'language język', run: () => setLanguage(language === 'pl' ? 'en' : 'pl') },
    ];
    const pages: Item[] = PAGES.map(({ page, icon: Icon }) => ({
      id: `page-${page}`,
      label: t.t(`nav.${page}`),
      icon: <Icon />,
      keywords: page,
      run: () => navigate({ page } as Route),
    }));

    // Transactions only once something is typed: by merchant or text, or by amount ("35,99").
    const found: Item[] = [];
    if (needle.length >= 2) {
      const amount = /^[\d\s.,]+$/.test(needle) ? needle.replace(/\s/g, '').replace(',', '.') : null;
      const categoryById = new Map(categories.map((c) => [c.id, c]));
      const hits = (transactions.data ?? [])
        .filter((x) => x.deletedAt === null)
        .filter((x) => {
          if (amount) return (Math.abs(Number(x.amountMinor)) / 100).toFixed(2).startsWith(amount);
          return fold(`${x.merchantName ?? ''} ${x.description ?? ''} ${x.descriptionRaw ?? ''}`).includes(needle);
        })
        .sort((a, b) => (a.bookingDate < b.bookingDate ? 1 : -1))
        .slice(0, 7);
      for (const x of hits) {
        const category = x.categoryId ? categoryById.get(x.categoryId) : undefined;
        found.push({
          id: `tx-${x.id}`,
          label: x.merchantName ?? x.description ?? '—',
          icon: <MerchantIcon texts={[x.merchantName, x.description]} icon={x.kind === 'TRANSFER' ? 'transfer' : (category?.icon ?? null)} color={x.kind === 'TRANSFER' ? '#4a3aa7' : (category?.color ?? null)} />,
          hint: (
            <span className="palette__meta">
              <Money minor={x.amountMinor} currency={x.currency} tone="signed" />
              <span>{t.date(x.bookingDate, 'dayMonth')}</span>
            </span>
          ),
          run: () => navigate({ page: 'transactions', id: x.id }),
        });
      }
    }
    return [
      { key: 'actions' as const, items: actions.filter(matches) },
      { key: 'pages' as const, items: pages.filter(matches) },
      { key: 'transactions' as const, items: found },
    ].filter((g) => g.items.length > 0);
  }, [query, t, resolved, privacy, language, banks.data, investments.data, transactions.data, categories, householdId, onQuickAdd, setTheme, setPrivacy, setLanguage, requestSync, toast]);

  const flat = groups.flatMap((g) => g.items);
  const current = Math.min(active, Math.max(0, flat.length - 1));

  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-index="${current}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const run = (item: Item | undefined) => {
    if (!item) return;
    onClose();
    void item.run();
  };

  const onKey = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (flat.length === 0) return;
      setActive((current + (event.key === 'ArrowDown' ? 1 : flat.length - 1)) % flat.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      run(flat[current]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  let index = -1;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="palette-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onMouseDown={(event) => event.target === event.currentTarget && onClose()}
        >
          <motion.div
            className="palette"
            role="dialog"
            aria-modal="true"
            aria-label={t.t('palette.title')}
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.985 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            onKeyDown={onKey}
          >
            <label className="palette__search">
              <Search aria-hidden="true" />
              <input
                autoFocus
                value={query}
                placeholder={t.t('palette.placeholder')}
                aria-label={t.t('palette.placeholder')}
                aria-activedescendant={flat[current] ? `palette-${flat[current]!.id}` : undefined}
                aria-controls="palette-list"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
              />
              <kbd>Esc</kbd>
            </label>
            <div className="palette__list" id="palette-list" role="listbox" ref={list}>
              {flat.length === 0 && <p className="palette__empty">{t.t('palette.empty', { query: query.trim() })}</p>}
              {groups.map((group) => (
                <div key={group.key} className="palette__group" role="group" aria-label={t.t(`palette.${group.key}`)}>
                  <span className="palette__heading">{t.t(`palette.${group.key}`)}</span>
                  {group.items.map((item) => {
                    index += 1;
                    const mine = index;
                    return (
                      <div
                        key={item.id}
                        id={`palette-${item.id}`}
                        role="option"
                        aria-selected={mine === current}
                        data-index={mine}
                        className="palette__item"
                        onMouseMove={() => mine !== current && setActive(mine)}
                        onClick={() => run(item)}
                      >
                        <span className="palette__icon" aria-hidden="true">
                          {item.icon}
                        </span>
                        <span className="palette__label truncate">{item.label}</span>
                        {item.hint}
                        {mine === current && <CornerDownLeft className="palette__enter" aria-hidden="true" />}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="palette__foot" aria-hidden="true">
              <span>
                <kbd>↑</kbd>
                <kbd>↓</kbd> {t.t('palette.hintMove')}
              </span>
              <span>
                <kbd>↵</kbd> {t.t('palette.hintOpen')}
              </span>
              <span>
                <kbd>Esc</kbd> {t.t('palette.hintClose')}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
